"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, CloudUpload, CircleCheck } from "lucide-react";
function IntakeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-primary/10 p-4 rounded-lg mb-6">
          <CircleCheck className="size-12 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">
          Message Sent
        </h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] rounded-lg border-primary text-primary hover:bg-primary/5">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" {...field} className="bg-background border-border focus:ring-primary rounded-lg" />
              </FormControl>
              <FormDescription className="text-muted-foreground/70">Please provide your first and last name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="info@cynthyleehealthcare.com" {...field} className="bg-background border-border focus:ring-primary rounded-lg" />
              </FormControl>
              <FormDescription className="text-muted-foreground/70">
                We will use this to reply to your inquiry.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="01273 000000" {...field} className="bg-background border-border focus:ring-primary rounded-lg" />
              </FormControl>
              <FormDescription className="text-muted-foreground/70">
                Optional – for a more personal consultation.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Please select a care type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Care Type Required</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background border-border focus:ring-primary rounded-lg">
                    <SelectValue placeholder="Select care service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Personal Care">Personal Care</SelectItem>
                  <SelectItem value="Live-in Care">Live-in Care</SelectItem>
                  <SelectItem value="Respite Care">Respite Care</SelectItem>
                  <SelectItem value="Elderly Care">Elderly Care</SelectItem>
                  <SelectItem value="Other">Other Inquiry</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="space-y-6">
        <FormField control={control} name="brief" rules={{
        required: "Message details are required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">How Can We Help?</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your care needs or any questions you have..." className="min-h-[120px] resize-y bg-background border-border focus:ring-primary rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/70">
                The more detail you provide, the better we can tailor our support for your family.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="assets" render={({
        field: {
          value,
          onChange,
          ...fieldProps
        }
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Relevant Documents (optional)</FormLabel>
              <FormControl>
                <div className="flex items-center justify-center w-full">
                  <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-secondary/30 border-border hover:bg-secondary/50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <CloudUpload className="w-8 h-8 mb-3 text-primary" />
                      <p className="mb-2 text-sm text-muted-foreground">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground">
                        PDF, PNG or JPG (MAX. 10MB)
                      </p>
                    </div>
                    <Input id="dropzone-file" type="file" className="hidden" onChange={e => {
                onChange(e.target.files);
              }} {...fieldProps} />
                  </label>
                </div>
              </FormControl>
              <FormDescription className="text-muted-foreground/70">
                Attach any care plans or medical summaries that might help us understand the situation.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <div className="mt-8 flex justify-end">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-md transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending Message...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function ProjectIntake() {
  return <section data-section-id="231639" id="send-a-message" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-lg blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/40 rounded-lg blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3" />

      <div className="container relative z-10 max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-tight">
            Get in Touch with Cynthylee
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Whether you're looking for care for a loved one or have questions about our services in Brighton & Hove, our compassionate team is here to help.
          </p>
        </div>

        <Card className="bg-card/80 backdrop-blur-xl border-border shadow-xl shadow-primary/5 overflow-hidden rounded-2xl">
          <CardHeader className="border-b border-border/30 bg-secondary/20 pb-8">
            <CardTitle className="text-2xl font-default text-foreground">Care Inquiry Form</CardTitle>
            <CardDescription className="text-base text-muted-foreground">
              Please fill out the details below and we will get back to you within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider formKey="send_message_form_contact" formId="send_message_form_contact" sectionName="send-a-message" defaultValues={{
            name: "",
            email: "",
            phone: "",
            project_type: "",
            brief: "",
            assets: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            project_type: {
              required: true
            },
            brief: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out to Cynthylee Health Care. Your message has been received, and one of our care coordinators will contact you shortly.">
              <IntakeForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>
    </section>;
}